import Vue from "vue/dist/vue.esm";
import axios from "axios";
import Toasted from "vue-toasted";
import _ from "lodash";

const http = axios.create({
  headers: {
    "Content-Type": "application/json",
  },
});

http.interceptors.request.use(
  (config) => {
    config.headers["X-CSRF-TOKEN"] = document
      .querySelector('meta[name="csrf-token"]')
      .getAttribute("content");
    return config;
  },
  (error) => Promise.reject(error.response)
);

Vue.use(Toasted, {
  position: "bottom-right",
  duration: 2000,
  singleton: true,
});

// Rack::Timeout kills a request at 30s and Puma answers 503; a proxy in front
// of it may answer 502/504 instead. All of them mean the same thing to a
// viewer of a large report: it took too long to build, not "there is no data".
function isTimeout(err) {
  const status = err && err.response && err.response.status;
  return status === 502 || status === 503 || status === 504 || !status;
}

const store = {
  namespaced: true,
  state: {
    saving: false,
    saved: false,
    errors: null,
    loading: false,
    loaded: false,
    editable: false,
    accessToken: null,
    // Per-tenant gate for the deliverable-tags report UI (chips, tag filter,
    // group-by-tag). Default false → feature hidden unless the share view seeds
    // it from the report owner's tenant flag.
    deliverableTagsEnabled: false,
    // deliverable id => post identity, only for cards that show the same post
    // as another card. Seeded by the report page for editors; empty otherwise.
    duplicatePosts: {},
    report: {
      id: null,
      name: "New report",
      sections: {
        description: null,
        removeDuplicates: true,
        deliverables: {
          filters: {
            calendars: [],
            events: [],
            excluded_deliverables: [],
            categories: [],
            users: [],
            tags: [],
            organizations: [],
            sponsors: [],
            providers: [],
            start_date: null,
            end_date: null,
          },
        },
        queryObject: {
          logicalOperator: "and",
          children: [],
        },
        // Owner-set view defaults; viewers inherit and may override per session.
        default_group_mode: "none",
        default_sort: "performance:desc",
      },
    },
    deliverables: [],
    cacheMeta: null,
    // A failed deliverable load used to be swallowed: the catch below reset
    // the spinner and nothing else, so a 500 (rack-timeout on a large report)
    // rendered as the "add a filter" empty state — indistinguishable from
    // "your content is gone". The report has to be able to say it failed.
    loadError: null,
  },
  getters: {
    filters(state) {
      return state.report.sections.deliverables.filters;
    },
    queryObject(state) {
      return state.report.sections.queryObject;
    },
    hasQueryObject(state, getters) {
      return getters.queryObject["children"].length > 0;
    },
    hasFilters(state, getters) {
      return (
        _.compact(_.map(_.values(getters.filters), (v) => !_.isEmpty(v)))
          .length > 0
      );
    },
    // Mirrors the Deliverable.with_reporting_errors scope (last_fetched IS NULL
    // OR last_error_at > last_fetched) and troubleshooter.vue's isFailing, so the
    // header badge, the Issues modal and the backend all agree on what's broken.
    //
    // The previous `!_.isEmpty(d.skip_refresh)` term made this permanently 0:
    // skip_refresh is a boolean, and lodash's isEmpty returns true for ANY
    // boolean, so `!isEmpty(...)` was false for both true and false. That silently
    // disabled the whole "N deliverables not pulling stats." half of the header
    // message. The intent was also inverted — skip_refresh means "don't refresh
    // this one", which excludes it from the error count rather than requiring it.
    deliverableErrorsCount(state) {
      return _.filter(_.values(state.deliverables), (d) => {
        if (_.isEmpty(d.url) || d.skip_refresh) return false;
        // Never attempted is its own bucket (deliverableNotAttemptedCount), so
        // the two counters stay disjoint — the header renders both.
        if (_.isEmpty(d.last_attempted_at)) return false;
        if (_.isEmpty(d.last_fetched)) return true;
        return (
          !_.isEmpty(d.last_error_at) &&
          new Date(d.last_error_at) > new Date(d.last_fetched)
        );
      }).length;
    },
    deliverableNotAttemptedCount(state) {
      return _.compact(
        _.map(_.values(state.deliverables), (d) => {
          return !_.isEmpty(d.url) && _.isEmpty(d.last_attempted_at);
        })
      ).length;
    },
    hasDeliverableErrors(state, getters) {
      return getters.deliverableErrorsCount > 0;
    },
    // The report's deliverable scope, shared by the report load and the
    // server-side search so both look at exactly the same set.
    // A saved report's removed posts are applied by the server from report_id
    // (Report#deliverables_scope). Listing them here as well put every id in
    // the GET, and a few hundred of them pushed the URL past the server's
    // limit: a bare 400 and an unloadable report.
    deliverablesScopeParams(state, getters) {
      return {
        query_object: getters.queryObject,
        report_id: state.report.id,
        access_token: state.accessToken,
        q: {
          calendar_id_in: getters.filters.calendars,
          event_id_in: getters.filters.events,
          id_not_in: state.report.id
            ? undefined
            : getters.filters.excluded_deliverables,
          user_id_in: getters.filters.users,
          normalized_inferred_type_in: getters.filters.providers,
          unscoped_event_event_category_id_in: getters.filters.categories,
          unscoped_event_tagged_with: getters.filters.tags,
          unscoped_event_organization_id_in: getters.filters.sponsors,
          unscoped_event_starts_at_dategteq: getters.filters.start_date,
          unscoped_event_starts_at_datelteq: getters.filters.end_date,
        },
      };
    },
  },
  mutations: {
    SHOW(state, report) {
      state.report = report;
      state.saved = true;
      return state;
    },
    SAVED(state, data) {
      state.report.id = data.id;
      state.saving = false;
      state.saved = true;
      return state;
    },
    ADD_DELIVERABLE_TAG(state, { ids, tag }) {
      const tagged = new Set(ids);
      state.deliverables.forEach((d) => {
        if (!tagged.has(d.id)) return;
        const tags = d.tag_list || [];
        if (!tags.includes(tag)) Vue.set(d, "tag_list", [...tags, tag]);
      });
      return state;
    },
    DELIVERABLES(state, deliverables) {
      state.loading = false;
      state.loaded = true;
      state.loadError = null;
      state.deliverables = deliverables;
      return state;
    },
    CACHE_META(state, meta) {
      state.cacheMeta = meta;
      return state;
    },
    LOAD_FAILED(state, error) {
      state.loading = false;
      state.loaded = false;
      state.loadError = error;
      return state;
    },
    SET_ACCESS_TOKEN(state, token) {
      state.accessToken = token;
      return state;
    },
    SET_EDITABLE(state, editable) {
      state.editable = editable;
      return state;
    },
    SET_DUPLICATE_POSTS(state, duplicatePosts) {
      state.duplicatePosts = duplicatePosts || {};
      return state;
    },
    SET_DELIVERABLE_TAGS_ENABLED(state, value) {
      state.deliverableTagsEnabled = !!value;
      return state;
    },
    SET_LOADED(state, loaded) {
      state.loaded = loaded;
      return state;
    },
    SET_SAVED(state, saved) {
      state.saved = saved;
      return state;
    },
  },
  actions: {
    // Ids of the report's deliverables matching a search term. Runs on the
    // server because it also searches article text the browser never gets.
    searchDeliverableIds({ getters }, term) {
      return http
        .get("/deliverables.json", {
          paramsSerializer(params) {
            return window.$.param(params);
          },
          params: {
            ...getters.deliverablesScopeParams,
            ids_only: true,
            search: term,
          },
        })
        .then((res) => res.data.ids || []);
    },
    // Adds one tag to each deliverable, keeping the tags they already have.
    bulkTagDeliverables({ commit }, { ids, tag }) {
      return http
        .post("/deliverables/bulk_tag.json", { ids, tag })
        .then((res) => {
          commit("ADD_DELIVERABLE_TAG", {
            ids: res.data.tagged_ids,
            tag: res.data.tag,
          });
          return res.data;
        });
    },
    setLoaded({ commit }, loaded) {
      commit("SET_LOADED", loaded);
    },
    setSaved({ commit }, saved) {
      commit("SET_SAVED", saved);
    },
    setEditable({ commit }, editable) {
      commit("SET_EDITABLE", editable);
    },
    setDuplicatePosts({ commit }, duplicatePosts) {
      commit("SET_DUPLICATE_POSTS", duplicatePosts);
    },
    setDeliverableTagsEnabled({ commit }, value) {
      commit("SET_DELIVERABLE_TAGS_ENABLED", value);
    },
    setAccessToken({ commit }, token) {
      commit("SET_ACCESS_TOKEN", token);
    },
    setDeliverables({ commit }, deliverables) {
      commit("DELIVERABLES", deliverables);
    },
    save({ commit, state }, report) {
      let url = `/reports`;
      let type = `post`;
      if (report.id) {
        url += `/${report.id}`;
        type = `patch`;
      }

      state.saving = true;
      state.errors = null;
      return new Promise((resolve, reject) => {
        window.$.ajax({
          url,
          type,
          dataType: "json",
          data: {
            report: {
              name: report.name,
              sections: JSON.stringify(report.sections),
            },
          },
          success(data) {
            commit("SAVED", data);
            resolve(data);
          },
          // Anything but a JSON body with `errors` in it — a 403 from Pundit,
          // a redirect to /billing, a 500 — used to throw right here on
          // `undefined.errors`, which meant `reject` never ran and every
          // caller's failure path (toast, spinner, retry) silently never
          // fired. A failed save has to be loud enough to reach the caller.
          error(xhr) {
            state.saving = false;
            state.errors =
              (xhr && xhr.responseJSON && xhr.responseJSON.errors) || null;
            reject(xhr);
          },
        });
      });
    },
    loadDeliverables({ commit, state, getters }) {
      if (
        getters.filters.events.length > 0 ||
        getters.hasFilters ||
        getters.hasQueryObject
      ) {
        state.loading = true;
        return new Promise((resolve, reject) => {
          http
            .get("/deliverables.json", {
              paramsSerializer(params) {
                return window.$.param(params);
              },
              params: {
                ...getters.deliverablesScopeParams,
                stats: true,
                async_warming: true,
              },
            })
            .then((res) => {
              // Response may be a bare array (uncached path) or an envelope
              // {deliverables, cache} when ReportStatsCache is engaged.
              if (Array.isArray(res.data)) {
                commit("DELIVERABLES", res.data);
                commit("CACHE_META", null);
              } else {
                commit("DELIVERABLES", res.data.deliverables || []);
                commit("CACHE_META", res.data.cache || null);
              }
              resolve(res);
            })
            .catch((err) => {
              // A rack-timeout arrives as a 503/500 with no JSON body. Say so
              // rather than falling through to an empty report.
              commit("LOAD_FAILED", {
                status: (err && err.response && err.response.status) || null,
                timeout: isTimeout(err),
              });
              reject(err);
            });
        });
      }
      return false;
    },
    show({ commit, dispatch, state }, report) {
      commit("SHOW", report);
      if (_.isEmpty(state.deliverables)) {
        dispatch("loadDeliverables");
      }
    },
  },
};

export default store;
